import { useState } from "react"
import toast from "react-hot-toast"
import {FileDown} from 'lucide-react'
import type { Equipment } from "../hooks/useEquipments"
import type { EquipmentIssue, EquipmentComment } from "../hooks/useEquipmentIssues"
import { generateEquipmentIssuePdf } from "../lib/equipmentIssuePdf"

interface Props {
  equipment: Equipment
  /** 현재 화면 필터가 적용된 이슈 목록 */
  filteredIssues: EquipmentIssue[]
  /** 장비의 전체 이슈 목록 */
  allIssues: EquipmentIssue[]
  /** 장비 단위로 로드된 전체 코멘트 */
  comments: EquipmentComment[]
  unitLabelMap: Map<string, string>
}

type ExportScope = "filtered" | "all"

export function EquipmentIssuePdfExport({
  equipment,
  filteredIssues,
  allIssues,
  comments,
  unitLabelMap,
}: Props) {
  const [showModal, setShowModal] = useState(false)
  const [scope, setScope] = useState<ExportScope>("filtered")
  const [includePhotos, setIncludePhotos] = useState(true)
  const [includeComments, setIncludeComments] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0 })

  const targetIssues = scope === "filtered" ? filteredIssues : allIssues

  const handleExport = async () => {
    if (targetIssues.length === 0) {
      toast.error("출력할 이슈가 없습니다.")
      return
    }
    setExporting(true)
    setProgress({ current: 0, total: targetIssues.length })
    try {
      const count = await generateEquipmentIssuePdf({
        equipment,
        issues: targetIssues,
        comments,
        unitLabelMap,
        includePhotos,
        includeComments,
        withCover: true,
        fileName: `${equipment.productName || "장비"}_이슈이력`,
        onProgress: (current, total) => setProgress({ current, total }),
      })
      toast.success(`이슈 ${count}건의 PDF가 저장되었습니다.`)
      setShowModal(false)
    } catch (e) {
      console.error("장비 이슈 PDF 생성 실패:", e)
      toast.error("PDF 생성에 실패했습니다. 잠시 후 다시 시도해주세요.")
    } finally {
      setExporting(false)
      setProgress({ current: 0, total: 0 })
    }
  }

  const percent =
    progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        title="이슈 이력을 PDF로 출력"
        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg text-sm transition-colors shadow-sm"
      >
        <FileDown className="w-4 h-4" />
        PDF 출력
      </button>

      {showModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            if (!exporting) setShowModal(false)
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">이슈 이력 PDF 출력</h3>
                <p className="text-xs text-gray-400 mt-0.5">{equipment.productName}</p>
              </div>
              <button
                type="button"
                disabled={exporting}
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* 출력 범위 */}
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">출력 범위</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={exporting}
                    onClick={() => setScope("filtered")}
                    className={`px-3 py-2.5 rounded-lg border text-sm text-left transition-colors disabled:opacity-60 ${
                      scope === "filtered"
                        ? "border-orange-500 bg-orange-50 text-orange-700"
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <span className="block font-semibold">현재 필터 결과</span>
                    <span className="block text-xs opacity-80 mt-0.5">{filteredIssues.length}건</span>
                  </button>
                  <button
                    type="button"
                    disabled={exporting}
                    onClick={() => setScope("all")}
                    className={`px-3 py-2.5 rounded-lg border text-sm text-left transition-colors disabled:opacity-60 ${
                      scope === "all"
                        ? "border-orange-500 bg-orange-50 text-orange-700"
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <span className="block font-semibold">장비 전체 이슈</span>
                    <span className="block text-xs opacity-80 mt-0.5">{allIssues.length}건</span>
                  </button>
                </div>
              </div>

              {/* 포함 항목 */}
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-2">포함 항목</p>
                <label className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={includePhotos}
                    disabled={exporting}
                    onChange={(e) => setIncludePhotos(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">
                    첨부 사진 포함
                    <span className="block text-xs text-gray-400">
                      끄면 파일명만 기재되어 더 빠르게 생성됩니다.
                    </span>
                  </span>
                </label>
                <label className="flex items-center gap-2.5 px-3 py-2.5 mt-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={includeComments}
                    disabled={exporting}
                    onChange={(e) => setIncludeComments(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">
                    코멘트 포함
                    <span className="block text-xs text-gray-400">이슈별 코멘트를 3번 구역에 출력합니다.</span>
                  </span>
                </label>
              </div>

              {/* 진행률 */}
              {exporting ? (
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                    <span>
                      {progress.current}/{progress.total} 이슈 처리 중...
                    </span>
                    <span className="tabular-nums font-semibold text-orange-600">{percent}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-200"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  이슈는 <span className="font-semibold text-gray-500">이슈 등록 → 확인중 → 조치완료 → 조치불가</span> 순서로
                  정렬되며, 각 이슈는 새 페이지에서 이슈 발생 / 조치 결과 / 코멘트 구역으로 나누어 출력됩니다.
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
              <button
                type="button"
                disabled={exporting}
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-40"
              >
                취소
              </button>
              <button
                type="button"
                disabled={exporting || targetIssues.length === 0}
                onClick={handleExport}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white text-sm font-semibold rounded-lg hover:from-orange-700 hover:to-amber-700 disabled:opacity-60"
              >
                {exporting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    생성 중...
                  </>
                ) : (
                  <>
                    <FileDown className="w-4 h-4" />
                    PDF 생성 ({targetIssues.length}건)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
